import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", phone: "", email: "", service_interest: "", source: "Website", status: "New", notes: "", next_follow_up: "" };

export default function Leads() {
  const [items, setItems] = useState([]);
  const [services, setServices] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = async () => setItems((await api.get("/leads")).data);
  useEffect(() => {
    load();
    api.get("/catalog/services").then(r => setServices(r.data.services));
  }, []);

  const save = async () => {
    try {
      const payload = { ...form, next_follow_up: form.next_follow_up || null };
      if (editId) await api.patch(`/leads/${editId}`, payload);
      else await api.post("/leads", payload);
      setOpen(false); setForm(empty); setEditId(null); load();
      toast.success("Saved");
    } catch (e) { toast.error("Save failed", { description: e.response?.data?.detail || e.message }); }
  };
  const remove = async (l) => { await api.delete(`/leads/${l.id}`); load(); };
  const edit = (l) => { setForm({ ...empty, ...l, next_follow_up: (l.next_follow_up || "").slice(0, 10) }); setEditId(l.id); setOpen(true); };

  const statusTone = {
    New: "bg-blue-100 text-blue-800",
    Contacted: "bg-amber-100 text-amber-800",
    "Follow-up": "bg-purple-100 text-purple-800",
    Converted: "bg-emerald-100 text-emerald-800",
    Lost: "bg-zinc-200 text-zinc-700",
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="text-[11px] tracking-[0.2em] uppercase text-zinc-500 font-semibold">Sales pipeline</div>
          <h1 className="font-heading font-black tracking-tight text-3xl sm:text-4xl mt-2">Leads &amp; Follow-ups</h1>
        </div>
        <Button data-testid="new-lead-btn" onClick={() => { setForm(empty); setEditId(null); setOpen(true); }} className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-none">
          <Plus size={16} className="mr-1" /> New Lead
        </Button>
      </header>

      {/* Mobile cards */}
      <div className="sm:hidden space-y-2" data-testid="leads-mobile-list">
        {items.map(l => (
          <div key={l.id} data-testid={`lead-card-${l.id}`} className="bg-white border border-zinc-200 p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-semibold text-sm truncate">{l.name}</div>
                <div className="text-[11px] text-zinc-500 tabular">{l.phone}</div>
              </div>
              <span className={`shrink-0 text-[10px] font-semibold px-2 py-0.5 ${statusTone[l.status] || "bg-zinc-100"}`}>{l.status}</span>
            </div>
            <div className="mt-2 grid grid-cols-2 gap-x-2 gap-y-1 text-[11px]">
              {l.service_interest ? <div className="col-span-2"><span className="text-zinc-400 uppercase tracking-wider text-[9px]">Interest</span><div>{l.service_interest}</div></div> : null}
              {l.source ? <div><span className="text-zinc-400 uppercase tracking-wider text-[9px]">Source</span><div>{l.source}</div></div> : null}
              {l.next_follow_up ? <div><span className="text-zinc-400 uppercase tracking-wider text-[9px]">Follow-up</span><div className="tabular">{l.next_follow_up}</div></div> : null}
              {l.notes ? <div className="col-span-2"><span className="text-zinc-400 uppercase tracking-wider text-[9px]">Notes</span><div className="text-zinc-600 truncate">{l.notes}</div></div> : null}
            </div>
            <div className="mt-2 pt-2 border-t border-zinc-100 flex justify-end gap-0.5">
              <button data-testid={`edit-lead-${l.id}-m`} onClick={() => edit(l)} className="p-1.5 hover:bg-zinc-100 rounded"><Pencil size={14} /></button>
              <button data-testid={`delete-lead-${l.id}-m`} onClick={() => remove(l)} className="p-1.5 hover:bg-rose-50 rounded"><Trash2 size={14} className="text-rose-600" /></button>
            </div>
          </div>
        ))}
        {!items.length && <div className="text-center py-8 text-sm text-zinc-500">No leads yet.</div>}
      </div>

      <div className="hidden sm:block border border-zinc-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50">
            <tr className="text-[10px] tracking-[0.14em] uppercase text-zinc-500 border-b border-zinc-200">
              {["Name", "Phone", "Interest", "Source", "Status", "Next Follow-up", "Notes", ""].map(h => (
                <th key={h} className="text-left font-semibold px-3 py-2.5">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="stripe-rows">
            {items.map(l => (
              <tr key={l.id} data-testid={`lead-row-${l.id}`} className="border-t border-zinc-100">
                <td className="px-3 py-2.5 font-medium">{l.name}</td>
                <td className="px-3 py-2.5 tabular">{l.phone}</td>
                <td className="px-3 py-2.5">{l.service_interest}</td>
                <td className="px-3 py-2.5 text-xs text-zinc-500">{l.source}</td>
                <td className="px-3 py-2.5">
                  <span className={`inline-flex px-2 py-0.5 text-[11px] font-semibold ${statusTone[l.status] || "bg-zinc-100"}`}>{l.status}</span>
                </td>
                <td className="px-3 py-2.5 tabular text-xs">{l.next_follow_up || "—"}</td>
                <td className="px-3 py-2.5 text-xs text-zinc-500 max-w-[240px] truncate">{l.notes}</td>
                <td className="px-3 py-2.5">
                  <button data-testid={`edit-lead-${l.id}`} onClick={() => edit(l)} className="p-1 hover:bg-zinc-100 rounded"><Pencil size={15} /></button>
                  <button data-testid={`delete-lead-${l.id}`} onClick={() => remove(l)} className="p-1 hover:bg-rose-50 rounded"><Trash2 size={15} className="text-rose-600" /></button>
                </td>
              </tr>
            ))}
            {!items.length && <tr><td colSpan={8} className="text-center py-12 text-sm text-zinc-500">No leads yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading tracking-tight text-2xl">{editId ? "Edit Lead" : "New Lead"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <F label="Name" v={form.name} onChange={(v) => setForm({ ...form, name: v })} tid="lead-name-input" />
            <F label="Phone" v={form.phone} onChange={(v) => setForm({ ...form, phone: v })} tid="lead-phone-input" />
            <F label="Email" v={form.email} onChange={(v) => setForm({ ...form, email: v })} tid="lead-email-input" />
            <div>
              <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold">Interest</Label>
              <Select value={form.service_interest} onValueChange={(v) => setForm({ ...form, service_interest: v })}>
                <SelectTrigger data-testid="lead-interest-select"><SelectValue placeholder="Select service" /></SelectTrigger>
                <SelectContent className="max-h-64">
                  {services.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold">Source</Label>
              <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
                <SelectTrigger data-testid="lead-source-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Website", "Referral", "Instagram", "Facebook", "Google", "Cold Call", "WhatsApp"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="lead-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["New", "Contacted", "Follow-up", "Converted", "Lost"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <F label="Next follow-up" type="date" v={form.next_follow_up || ""} onChange={(v) => setForm({ ...form, next_follow_up: v })} tid="lead-followup-input" />
            <div className="col-span-2">
              <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold">Notes</Label>
              <Textarea data-testid="lead-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="lead-save-btn" onClick={save} className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-none">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function F({ label, v, onChange, tid, type = "text" }) {
  return (
    <div>
      <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold">{label}</Label>
      <Input data-testid={tid} type={type} value={v} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}
